// Third-party
import React, { Component } from 'react';
import { Route, RouteComponentProps, withRouter, Redirect, Switch } from 'react-router';
import { connect } from 'react-redux';

// Utils
import { formatMessage } from '../../../../utils/i18n';
import { saveEnterpriseGroupInfos } from '../../../../redux/modules/enterprise/admin/groups';
import EnterpriseMembers from '../enterprise/enterprise-members/EnterpriseMembers';
import EnterprisePortfolios from '../enterprise/EnterprisePortfolios';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '../../../../utils/permissions';

// Types
import { ReduxState } from '../../../../redux/modules/types';
import EnterpriseAdminGroup from '@/types/enterprise-admin-group';
import EnterpriseGroupInfos from '@/types/enterprise-group-infos';
import EnterpriseInfos from '@/types/enterprise-infos';
import NavigationItem from '@/types/navigation-item';
import User from '@/types/user';

// TODO: @deprecate when we deprecate Legacy
import LegacyEnterpriseGroupDetails from './LegacyEnterpriseGroupDetails';
import useThemeVersion from '@/hooks/useThemeVersion';

enum ADMIN_ENTERPRISE_TABS {
  MEMBERS = 'members',
  MODELS = 'models',
}

type State = {};

type DispatchProps = {
  saveEnterpriseGroupInfos: (groupId: string, infos: EnterpriseGroupInfos) => Promise<EnterpriseGroupInfos>;
};

type StateProps = {
  group?: EnterpriseAdminGroup;
};

type ParentProps = {
  pills?: NavigationItem[];
  user: User;
};

type RouteProps = RouteComponentProps<{ id: string }>;

type Props = StateProps & ParentProps & DispatchProps & RouteProps;

class EnterpriseGroupDetails extends Component<Props, State> {
  readonly state: State = {};

  onSaveInfo = (infos: EnterpriseInfos) => {
    const { group } = this.props;

    if (!group) {
      return Promise.reject();
    }

    return this.props.saveEnterpriseGroupInfos(group._id, infos);
  };

  readonly memberAdmin = userHasPermissionEnabled(
    this.props.user,
    PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS,
    PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS_ADD,
    PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS_REMOVE,
  );

  readonly modelAdmin = userHasPermission(this.props.user, PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MODELS);

  readonly i18nBasePath = 'Admin.Enterprise';

  readonly redirect = this.memberAdmin ? 'members' : 'models';

  readonly membersNavPath = `${this.props.match.url}/${ADMIN_ENTERPRISE_TABS.MEMBERS}`;

  readonly modelsNavPath = `${this.props.match.url}/${ADMIN_ENTERPRISE_TABS.MODELS}`;

  getSelectedAdminEnterpriseTab = () => {
    switch (window.location.pathname) {
      case this.membersNavPath: {
        return ADMIN_ENTERPRISE_TABS.MEMBERS;
      }
      case this.modelsNavPath: {
        return ADMIN_ENTERPRISE_TABS.MODELS;
      }
      default: {
        return ADMIN_ENTERPRISE_TABS.MEMBERS;
      }
    }
  };

  getAdminEnterpriseTabsOptions = () => [
    {
      id: ADMIN_ENTERPRISE_TABS.MEMBERS,
      value: this.membersNavPath,
      label: formatMessage(`${this.i18nBasePath}.TeamMembers`),
      i18nPath: `${this.i18nBasePath}.TeamMembers`,
      disabled: !this.memberAdmin,
    },
    {
      id: ADMIN_ENTERPRISE_TABS.MODELS,
      value: this.modelsNavPath,
      label: formatMessage(`${this.i18nBasePath}.PortfoliosTitle`),
      i18nPath: `${this.i18nBasePath}.PortfoliosTitle`,
      disabled: !this.modelAdmin,
    },
  ];

  render() {
    const {
      group,
      user,
      match: { url },
    } = this.props;

    if (!group) {
      return null;
    }

    const memberAdmin = userHasPermissionEnabled(
      user,
      PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS,
      PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS_ADD,
      PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS_REMOVE,
    );
    const modelAdmin = userHasPermission(user, PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MODELS);
    const redirect = memberAdmin ? 'members' : 'models';

    return (
      <div className="enterprise-group-details">
        <div>
          <Switch>
            {memberAdmin && (
              <Route path={`${url}/members`}>
                <EnterpriseMembers
                  isGroup
                  adminEnterpriseTabOptions={this.getAdminEnterpriseTabsOptions()}
                  group={group}
                  pills={this.props.pills}
                  selectedAdminEnterpriseTab={this.getSelectedAdminEnterpriseTab()}
                  onSave={this.onSaveInfo}
                />
              </Route>
            )}
            {modelAdmin && (
              <Route path={`${url}/models`}>
                <EnterprisePortfolios
                  isGroup
                  adminEnterpriseTabOptions={this.getAdminEnterpriseTabsOptions()}
                  group={group}
                  pills={this.props.pills}
                  selectedAdminEnterpriseTab={this.getSelectedAdminEnterpriseTab()}
                  user={user}
                  onSave={this.onSaveInfo}
                />
              </Route>
            )}

            <Redirect to={`${url}/${redirect}`} />
          </Switch>
        </div>
      </div>
    );
  }
}

const EnterpriseGroupDetailsComponent = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <EnterpriseGroupDetails {...props} />;
  }
  return <LegacyEnterpriseGroupDetails {...props} />;
};

const mapStateToProps = (state: ReduxState, ownProps: RouteProps) => {
  const groups = state.enterprise.admin ? state.enterprise.admin.groups : [];
  return {
    group: groups.find(g => g._id === ownProps.match.params.id),
  };
};

export default withRouter(
  connect(mapStateToProps, {
    saveEnterpriseGroupInfos,
  })(EnterpriseGroupDetailsComponent),
);
